import React, { useState, useEffect, useCallback } from 'react';
import { assetLocationsApi } from '../../api';
import { AssetLocation, CreateAssetLocationData, UpdateAssetLocationData } from '../../types';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { Icons } from '../../components/common/Icons';
import { usePermissions } from '../../hooks/usePermissions';
import './AssetCategories.css';

export function AssetLocations() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('asset_locations.manage');
  
  const [locations, setLocations] = useState<AssetLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<AssetLocation | null>(null);
  const [formData, setFormData] = useState<CreateAssetLocationData>({
    name: '',
    address: '',
  });

  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await assetLocationsApi.getLocations({ include_inactive: showInactive });
      setLocations(res.locations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingLocation) {
        await assetLocationsApi.updateLocation(editingLocation.id, formData as UpdateAssetLocationData);
      } else {
        await assetLocationsApi.createLocation(formData);
      }
      await fetchLocations();
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save location');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      setLoading(true);
      await assetLocationsApi.deleteLocation(id);
      await fetchLocations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete location');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (location: AssetLocation) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address || '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingLocation(null);
    setFormData({ name: '', address: '' });
  };

  return (
    <div className="asset-categories-page">
      <div className="page-header">
        <div className="page-title">
          <h1>Asset Locations</h1>
          <p>Manage office locations for assets</p>
        </div>
        {canManage && (
          <Button onClick={() => setShowModal(true)}>
            <Icons.Plus /> Add Location
          </Button>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <Card>
        <div className="toolbar">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show Inactive
          </label>
        </div>

        {loading ? (
          <div className="loading-state">Loading locations...</div>
        ) : locations.length === 0 ? (
          <div className="empty-state">
            <Icons.MapPin />
            <h3>No locations found</h3>
            <p>Add your first location to get started</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Status</th>
                  {canManage && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {locations.map(location => (
                  <tr key={location.id} className={!location.is_active ? 'inactive-row' : ''}>
                    <td className="font-medium">{location.name}</td>
                    <td>{location.address || '-'}</td>
                    <td>
                      <span className={`status-badge ${location.is_active ? 'active' : 'inactive'}`}>
                        {location.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canManage && (
                      <td>
                        <div className="action-buttons">
                          <button className="icon-button" onClick={() => openEditModal(location)} title="Edit">
                            <Icons.Edit />
                          </button>
                          <button className="icon-button delete-btn" onClick={() => handleDelete(location.id)} title="Delete">
                            <Icons.Trash />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingLocation ? 'Edit Location' : 'Add Location'}</h2>
              <button className="close-button" onClick={resetForm}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Gurgaon Office"
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={3}
                    placeholder="Full address..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={resetForm}>Cancel</Button>
                <Button type="submit" disabled={loading}>{editingLocation ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssetLocations;
